import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { INICIO } from '../../core/router/routes';
import { Avatar, ButtonBase, Divider, ListItemText, Menu, MenuItem, Typography } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';
import { useCourseCore } from '../../core/modules/course';
import { useMeCore } from '../../core/modules/me';
import { useNavigationCore } from '../../core/router/navigation';
import { Course } from '../../core/interfaces/course.interface';

/** Selector del curso actual: reemplaza lo que hacía la barra negra (moverse entre cursos). */
const CourseSelector = ({ compact = false }: { compact?: boolean }) => {
  const { course, courses, getCourse, getCourses, setCurrentClassId } = useCourseCore();
  const { pathname } = useLocation();
  const { user, studentCourses, profesorCourses, getMeCourses } = useMeCore();
  const { goToMeCourse, goToInicio } = useNavigationCore();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  const userCourses: Course[] = useMemo(() => {
    if (!user) return [];
    if (user.userRole.admin) return courses;
    if (user.userRole.student) return studentCourses.map((studentCourse) => studentCourse.course);
    if (user.userRole.professor) return profesorCourses;
    return [];
  }, [courses, profesorCourses, studentCourses, user]);

  const open = (event: React.MouseEvent<HTMLElement>) => {
    setAnchor(event.currentTarget);
    if (!userCourses.length && user) {
      if (user.userRole.admin) {
        getCourses({});
      } else {
        getMeCourses();
      }
    }
  };

  const select = (selected: Course) => {
    setAnchor(null);
    setCurrentClassId('');
    // En el Inicio, cambiar de curso actualiza el Inicio; en el resto lleva a las clases del curso
    if (pathname === INICIO) {
      getCourse(selected.id);
    } else {
      goToMeCourse(selected.id);
    }
  };

  return (
    <>
      <ButtonBase
        onClick={open}
        aria-haspopup="true"
        aria-label="Cambiar de curso"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          border: '1px solid #E2E8F0',
          backgroundColor: '#fff',
          borderRadius: 999,
          pl: 0.5,
          pr: 1.25,
          py: 0.5,
          mx: compact ? 0 : 1,
          flexShrink: 1,
          minWidth: 0,
          maxWidth: compact ? 170 : 300,
        }}
      >
        {course ? (
          <Avatar src={course.avatar} alt="" sx={{ width: 28, height: 28 }} />
        ) : null}
        <Typography noWrap sx={{ fontWeight: 600, fontSize: 13.5, color: '#0F172A', pl: course ? 0 : 1 }}>
          {course ? course.name : 'Mis cursos'}
        </Typography>
        <KeyboardArrowDownIcon sx={{ color: '#64748B', fontSize: 20, flexShrink: 0 }} />
      </ButtonBase>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { width: 340, maxWidth: 'calc(100vw - 24px)', borderRadius: 3, mt: 1 } }}
      >
        <Typography sx={{ px: 2, pt: 1, pb: 0.5, fontSize: 12.5, fontWeight: 600, color: '#64748B' }}>Tus cursos</Typography>
        {userCourses.map((item) => {
          const current = item.id === course?.id;
          return (
            <MenuItem key={item.id} onClick={() => select(item)} selected={current} sx={{ gap: 1.5, py: 1, whiteSpace: 'normal' }}>
              <Avatar variant="rounded" src={item.avatar} alt="" sx={{ width: 40, height: 40 }} />
              <ListItemText primary={item.name} primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }} />
              {current ? <CheckIcon sx={{ color: '#4F46E5' }} fontSize="small" /> : null}
            </MenuItem>
          );
        })}
        <Divider />
        <MenuItem onClick={() => { setAnchor(null); goToInicio(); }} sx={{ color: '#4F46E5', fontWeight: 600, fontSize: 14 }}>
          Ir al inicio
        </MenuItem>
      </Menu>
    </>
  );
};

export default CourseSelector;
